import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { SITE } from "../data/site";
import { Lock } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { AddToHomeScreen } from "../components/AddToHomeScreen";

export default function Login() {
  const { login, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If the saved session is still valid, jump straight to the dashboard.
  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-blue px-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <img src={SITE.logo} alt="Nica Moto Rentals" className="h-16 mx-auto mb-4" />
          <h1 className="font-heading font-black text-2xl text-brand-gray flex items-center justify-center gap-2">
            <Lock size={20} className="text-brand-orange" /> {t.login.title}
          </h1>
          <p className="text-slate-400 text-sm mt-1">{t.login.subtitle}</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-heading font-semibold text-brand-gray">{t.login.email}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-blue"
              placeholder="admin@nicamotorentals.com"
            />
          </div>
          <div>
            <label className="text-sm font-heading font-semibold text-brand-gray">{t.login.password}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-blue"
              placeholder="••••••••"
            />
          </div>
          {error && <p className="text-red-500 text-sm" data-testid="login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white font-heading font-bold py-3 rounded-full transition-colors"
          >
            {loading ? t.login.signing : t.login.signin}
          </button>
        </form>

        <AddToHomeScreen />
      </div>
    </div>
  );
}
